import React, { useState } from 'react';
import { Handle, Position } from 'reactflow';
import { WorkflowStep } from '../../types/workflow';
import { Plus, Settings, Globe, Brain, Code, Trash2, ChevronDown, GripVertical } from 'lucide-react';

interface N8nWorkflowStackProps {
  data: {
    customSteps: WorkflowStep[];
    onAddNode: (stepType: string) => void;
    onShowNodePanel: () => void;
    onEditStep: (step: WorkflowStep) => void;
    onDeleteStep: (stepName: string) => void;
    onReorderSteps?: (steps: WorkflowStep[]) => void;
  };
}

const N8nWorkflowStack: React.FC<N8nWorkflowStackProps> = ({ data }) => {
  const { customSteps, onAddNode, onShowNodePanel, onEditStep, onDeleteStep, onReorderSteps } = data;
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const getNodeIcon = (step: WorkflowStep) => {
    if (step.name.includes('api') || step.type === 10) return <Globe size={14} className="text-green-600" />;
    if (step.name.includes('ai')) return <Brain size={14} className="text-blue-600" />;
    if (step.name.includes('evm') || step.type === 11) return <Code size={14} className="text-orange-600" />;
    return <Settings size={14} className="text-gray-600" />;
  };

  const getNodeColor = (step: WorkflowStep) => {
    if (step.name.includes('api') || step.type === 10) return 'bg-green-50 border-green-200';
    if (step.name.includes('ai')) return 'bg-blue-50 border-blue-200';
    if (step.name.includes('evm') || step.type === 11) return 'bg-orange-50 border-orange-200';
    return 'bg-gray-50 border-gray-200';
  };

  const EmptySlot = ({ index }: { index: number }) => (
    <div 
      className="bg-n8n-dark-700 border-2 border-dashed border-n8n-dark-600 rounded-n8n hover:border-n8n-primary-400 hover:bg-n8n-primary-900/30 transition-all duration-200 h-12 flex items-center justify-center cursor-pointer group shadow-n8n"
      onClick={() => onShowNodePanel()}
    >
      <div className="flex items-center text-n8n-dark-500 group-hover:text-n8n-primary-400 transition-colors">
        <Plus size={16} className="mr-2" />
        <span className="text-xs font-medium">Add Step {index + 1}</span>
      </div>
    </div>
  );

  // Mouse-based drag and drop handlers (to avoid ReactFlow conflicts)
  const handleMouseDown = (e: React.MouseEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    setDraggedIndex(index);
    setIsDragging(true);
    
    let currentDragOverIndex: number | null = null;
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      // Find which step we're hovering over
      const elements = document.elementsFromPoint(moveEvent.clientX, moveEvent.clientY);
      const stepElement = elements.find(el => el.getAttribute('data-step-index'));
      
      if (stepElement) {
        const hoverIndex = parseInt(stepElement.getAttribute('data-step-index') || '0');
        currentDragOverIndex = hoverIndex;
        setDragOverIndex(hoverIndex);
      }
    };

    const handleMouseUp = () => {
      if (currentDragOverIndex !== null && index !== currentDragOverIndex) {
        const newSteps = [...customSteps];
        const draggedStep = newSteps[index];
        
        // Remove dragged item
        newSteps.splice(index, 1);
        
        // Insert at new position
        newSteps.splice(currentDragOverIndex, 0, draggedStep);
        
        if (onReorderSteps) {
          onReorderSteps(newSteps);
        }
      }
      
      setDraggedIndex(null);
      setIsDragging(false);
      setDragOverIndex(null);
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const WorkflowNode = ({ step, index }: { step: WorkflowStep; index: number }) => (
    <div 
      data-step-index={index}
      className={`bg-n8n-dark-700 border border-n8n-dark-600 rounded-n8n p-3 shadow-n8n hover:shadow-n8n-lg transition-all duration-200 group hover:border-n8n-dark-500 ${
        draggedIndex === index ? 'opacity-50' : ''
      } ${
        dragOverIndex === index && draggedIndex !== index ? 'border-n8n-primary-400 bg-n8n-primary-900/20' : ''
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          {onReorderSteps && (
            <div 
              className="flex-shrink-0 opacity-50 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing p-1 -m-1 rounded hover:bg-n8n-dark-600 select-none"
              onMouseDown={(e) => handleMouseDown(e, index)}
              title="Drag to reorder"
            >
              <GripVertical size={14} className="text-n8n-dark-400" />
            </div>
          )}
          <div className="flex-shrink-0">
            {getNodeIcon(step)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-medium text-n8n-dark-200 text-sm truncate">{step.name}</div>
            <div className="text-xs text-n8n-dark-400">
              {step.type === 10 ? 'HTTP' : step.type === 11 ? 'EVM' : 'Custom'}
            </div>
          </div>
        </div>
        <div className="opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
          <button 
            onClick={() => onEditStep(step)}
            className="p-1 hover:bg-n8n-dark-700 rounded transition-colors duration-200"
          >
            <Settings size={12} className="text-n8n-dark-300" />
          </button>
          <button 
            onClick={() => onDeleteStep(step.name)}
            className="p-1 hover:bg-red-900/30 rounded transition-colors duration-200"
          >
            <Trash2 size={12} className="text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );

  const items = customSteps.length === 0 ? 
    Array.from({ length: 3 }, (_, i) => ({ type: 'empty' as const, index: i })) :
    customSteps.map((step, index) => ({ type: 'step' as const, step, index }));

  return (
    <div className="group relative">
      {/* Workflow Stack Container */}
      <div className="bg-n8n-primary-900/20 border-2 border-dashed border-n8n-primary-400/50 rounded-n8n p-4 min-w-[260px] shadow-n8n-lg hover:shadow-n8n-lg transition-all duration-200">
        {/* Stack Header */}
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-n8n-primary-400/30">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-n8n-primary-500 rounded-full flex items-center justify-center shadow-n8n">
              <span className="text-white text-xs font-bold">W</span>
            </div>
            <span className="font-semibold text-n8n-primary-200 text-sm">Workflows</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs text-n8n-primary-300 bg-n8n-primary-800/50 px-2 py-1 rounded-full">
              {customSteps.length} steps
            </span>
            {/* ReactFlow drag handle - for moving the entire stack */}
            <div className="workflow-stack-drag-handle cursor-move opacity-50 hover:opacity-100 transition-opacity p-1 -m-1 rounded hover:bg-n8n-primary-600/20" title="Drag to move workflow stack">
              <GripVertical size={12} className="text-n8n-primary-400" />
            </div>
          </div>
        </div>

        {/* Stack Items */}
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="relative">
              {item.type === 'empty' ? (
                <EmptySlot index={item.index} />
              ) : (
                <WorkflowNode step={item.step} index={item.index} />
              )}
              
              {/* Vertical Arrow between items */}
              {index < items.length - 1 && (
                <div className="flex justify-center py-1">
                  <ChevronDown size={16} className="text-n8n-primary-400/70" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Connection Handles */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-n8n-primary-500 border-2 border-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-n8n"
        style={{ left: -8 }}
      />
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-n8n-primary-500 border-2 border-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-n8n"
        style={{ right: -8 }}
      />
    </div>
  );
};

export default N8nWorkflowStack;